import axios, {
    AxiosInstance,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios'

import {
    recoverAccessToken,
    storedSessionUserId,
} from 'features/Auth/sessionRefresh'

declare module 'axios' {
    interface AxiosRequestConfig {
        sessionRecoveryAttempted?: boolean
        sessionUserId?: string | null
    }
}

const BEARER_PREFIX = 'Bearer '
const OAUTH_PATH_SEGMENT = '/oauth/'

function sentBearerToken(config: InternalAxiosRequestConfig): string | null {
    const authorization = config.headers.Authorization
    return typeof authorization === 'string' &&
        authorization.startsWith(BEARER_PREFIX)
        ? authorization.slice(BEARER_PREFIX.length)
        : null
}

// The oauth endpoints answer 401 for reasons of their own (an expired MFA
// challenge, bad client credentials) that a refreshed session cannot fix.
function isRecoverableUnauthorized(
    config: InternalAxiosRequestConfig
): boolean {
    return (
        !config.sessionRecoveryAttempted &&
        !(config.url ?? '').includes(OAUTH_PATH_SEGMENT) &&
        sentBearerToken(config) !== null
    )
}

export const createApiClient = (): AxiosInstance => {
    const client = axios.create({
        baseURL: CEREBRO_API_ENDPOINT,
        validateStatus: (status) => status >= 200 && status < 500,
        headers: {
            'Content-Type': 'application/json',
            // Identifies Forge as the calling application on Cerebro requests.
            // Cerebro uses this client-name marker for request attribution across
            // features — e.g. banner analytics (application_name) and the
            // origin_app_id it stamps into the Atlas/hulk JWT for cross-service
            // tracing.
            'x-dsi-client-name': 'forge',
        },
    })

    // Recorded at send time: by the time a 401 comes back, another tab may
    // already have signed in as someone else.
    client.interceptors.request.use((config) => {
        if (config.sessionUserId === undefined) {
            config.sessionUserId = storedSessionUserId()
        }
        return config
    })

    const replayWithRecoveredSession = async (
        config: InternalAxiosRequestConfig
    ): Promise<AxiosResponse | null> => {
        if (!isRecoverableUnauthorized(config)) {
            return null
        }
        const accessToken = await recoverAccessToken(
            sentBearerToken(config),
            config.sessionUserId ?? null
        )
        if (!accessToken) {
            return null
        }
        config.sessionRecoveryAttempted = true
        config.headers.Authorization = `${BEARER_PREFIX}${accessToken}`
        return client(config)
    }

    // Cerebro access tokens expire ten hours after sign-in. On the first 401
    // the client refreshes the session and replays the request once; only a
    // 401 that survives that reaches the callers' sign-out handling. A 401
    // arrives as a response, or as an error from a request whose own
    // validateStatus rejects it.
    client.interceptors.response.use(
        async (response) => {
            if (response.status !== 401) {
                return response
            }
            return (
                (await replayWithRecoveredSession(response.config)) ?? response
            )
        },
        async (error: unknown) => {
            if (
                !axios.isAxiosError(error) ||
                error.response?.status !== 401 ||
                !error.config
            ) {
                throw error
            }
            const replayed = await replayWithRecoveredSession(error.config)
            if (!replayed) {
                throw error
            }
            return replayed
        }
    )

    return client
}
